/** Finance → Tax reports (SEZ / SEZWOP / SEZWP / Export split).
 *
 *  Reuses /api/sez/reports/turnover — Finance-role gated.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Receipt, RefreshCw, ChevronRight } from "lucide-react";

const fmtINR = (n) => new Intl.NumberFormat("en-IN",
  { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const TONE = {
  REGULAR:                     "bg-slate-50 text-slate-800 border-slate-200",
  SEZWOP:                      "bg-emerald-50 text-emerald-800 border-emerald-200",
  SEZWP:                          "bg-emerald-50 text-emerald-800 border-emerald-200",
  EXPORT:                        "bg-blue-50 text-blue-800 border-blue-200",
  REVIEW_REQUIRED: "bg-rose-50 text-rose-800 border-rose-200",
};

export default function TaxReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sp, setSp] = useSearchParams();
  const start = sp.get("start") || "";
  const end = sp.get("end") || "";
  const reversed = Boolean(start && end && start > end);
  const setRange = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n, { replace: true }); };
  const setStart = (v) => setRange("start", v);
  const setEnd = (v) => setRange("end", v);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (start && end && start > end) return;
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (start) p.set("start", start);
      if (end)   p.set("end", end);
      const { data } = await api.get(`/sez/reports/turnover?${p}`);
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  }, [start, end]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => (reversed ? toast.error("‘From’ date is after ‘To’ date — correct the range to refresh.") : load());

  return (
    <div className="space-y-4 max-w-[1400px]" data-testid="tax-reports-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
            Finance · Tax reports
          </div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 mt-0.5">
            Turnover by GST treatment
          </h1>
          <div className="text-[13px] text-slate-600 mt-1 max-w-2xl">
            Split of quotations, customer POs and orders across Regular GST,
            SEZ (with / without payment), Export and unresolved treatments.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500">From</div>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
              data-testid="tax-start" className="h-9 px-2 border border-slate-200 rounded text-[12px]" />
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500">To</div>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              data-testid="tax-end" className="h-9 px-2 border border-slate-200 rounded text-[12px]" />
          </div>
          {reversed && <div role="alert" className="self-end rounded border border-rose-300 bg-rose-50 px-2 py-1.5 text-[11px] text-rose-800" data-testid="tax-range-error">‘From’ ({start}) is after ‘To’ ({end}). {data ? "Figures below are from the last valid range." : "Correct the range to load figures."}</div>}
          <Button variant="outline" onClick={refresh} disabled={loading}
            data-testid="tax-refresh" className="h-9 self-end">
            <RefreshCw className={"h-3.5 w-3.5 mr-1 " + (loading ? "animate-spin" : "")} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="border border-slate-200 rounded-md bg-white overflow-hidden">
        <div className="grid grid-cols-[160px_repeat(6,1fr)_36px] px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
          <div>Treatment</div>
          <div className="text-right">Quotes #</div>
          <div className="text-right">Quotes value</div>
          <div className="text-right">POs #</div>
          <div className="text-right">POs value</div>
          <div className="text-right">Orders #</div>
          <div className="text-right">Orders value</div>
          <div />
        </div>
        <div className="divide-y divide-slate-100">
          {(data?.rows || []).map((r) => (
            <div key={r.treatment}
              data-testid={`tax-row-${r.treatment}`}
              className="grid grid-cols-[160px_repeat(6,1fr)_36px] px-3 py-2 items-center hover:bg-slate-50">
              <div>
                <span className={"text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-semibold " +
                  (TONE[r.treatment] || TONE.REGULAR)}>
                  {r.treatment.replace(/_/g, " ")}
                </span>
              </div>
              <div className="text-right tabular-nums text-[13px]">{r.quotations_count}</div>
              <div className="text-right tabular-nums font-mono text-[12px]">{fmtINR(r.quotations_value)}</div>
              <div className="text-right tabular-nums text-[13px]">{r.customer_po_count}</div>
              <div className="text-right tabular-nums font-mono text-[12px]">{fmtINR(r.customer_po_value)}</div>
              <div className="text-right tabular-nums text-[13px]">{r.orders_count}</div>
              <div className="text-right tabular-nums font-mono text-[12px]">{fmtINR(r.orders_value)}</div>
              <button onClick={() => navigate(
                  `/commercial/crm/quotations?gst_treatment=${r.treatment}`)}
                data-testid={`tax-drill-${r.treatment}`}
                className="text-slate-400 hover:text-emerald-700 flex justify-end">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="text-[10px] text-slate-500">
        Generated {data?.generated_at ? new Date(data.generated_at).toLocaleString() : "—"}
        {" · "}Quoted value ≠ revenue. Only Finance / Tax / Super Admin can view this report.
      </div>
    </div>
  );
}
